import type { CSSProperties } from 'react';
import type { TemplateBackground } from '@/templates/domain/types/TemplateBackground';
import { BACKGROUND_COLORS } from '@/common/presentation/constants/colors';
import { assertSafeImageUrl } from '@/common/domain/utils/assertSafeImageUrl';

interface ResolvePageBackgroundStyleInput {
  background?: TemplateBackground;
  /** True when this page hosts a cover_page block (not merely pageNumber === 1). */
  isCoverPage: boolean;
}

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_COLOR_REGEX = /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(?:\d*\.\d+|\d+))?\s*\)$/i;
const HSL_COLOR_REGEX = /^hsla?\(\s*[-\d.]+\s*(?:deg)?\s*,\s*[-\d.]+%\s*,\s*[-\d.]+%(?:\s*,\s*(?:\d*\.\d+|\d+))?\s*\)$/i;
/** Allowlist CSS linear-gradient — no url(), expressions, or statement breakouts. */
const SAFE_LINEAR_GRADIENT_REGEX = /^linear-gradient\([^;{}]*\)$/i;

function shouldApplyBackground(background: TemplateBackground | undefined, isCoverPage: boolean): boolean {
  if (!background || background.kind === 'none') return false;
  if (isCoverPage && background.applyToCover === false) return false;
  return true;
}

function toCssImageUrl(imageUrl: string | null | undefined): string | undefined {
  const safe = assertSafeImageUrl(imageUrl);
  if (!safe) return undefined;
  const escaped = safe.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

function isCssColorValue(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return HEX_COLOR_REGEX.test(trimmed) || RGB_COLOR_REGEX.test(trimmed) || HSL_COLOR_REGEX.test(trimmed);
}

function isSafeGradientValue(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!SAFE_LINEAR_GRADIENT_REGEX.test(trimmed)) return undefined;
  if (/url\s*\(|expression\s*\(|javascript:/i.test(trimmed)) return undefined;
  return trimmed;
}

export function resolvePageBackgroundStyle({
  background,
  isCoverPage
}: ResolvePageBackgroundStyleInput): Pick<
  CSSProperties,
  'background' | 'backgroundColor' | 'backgroundImage' | 'backgroundSize' | 'backgroundPosition' | 'backgroundRepeat'
> {
  if (!background || !shouldApplyBackground(background, isCoverPage)) return {};

  const safeColor = isCssColorValue(background.color) ? background.color?.trim() : undefined;

  switch (background.kind) {
    case 'color':
      return {
        backgroundColor: safeColor ?? BACKGROUND_COLORS.WHITE
      };
    case 'gradient': {
      const safeGradient = isSafeGradientValue(background.gradient);
      return {
        background: safeGradient || safeColor || BACKGROUND_COLORS.WHITE
      };
    }
    case 'image': {
      // Prefer imageUrl; fall back to imageAssetId when hosts store an http(s) URL there.
      const cssImage = toCssImageUrl(background.imageUrl) ?? toCssImageUrl(background.imageAssetId);
      if (!cssImage) {
        return safeColor ? { backgroundColor: safeColor } : {};
      }

      // Keep in sync with PDF DocumentBuilderPdfDocument background Image:
      // objectFit: 'cover' + objectPosition: 'center' (see LLAMS-274 verification remediation).
      return {
        backgroundColor: safeColor ?? BACKGROUND_COLORS.WHITE,
        backgroundImage: cssImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }
    default:
      return {};
  }
}
